import { Component, OnInit, Input, Output, EventEmitter, inject, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Attachment } from '../../objects/attachment';
import { AttachmentViewerComponent } from '../../secure/entity-viewers/attachment-viewer/attachment-viewer.component';
import { NewAttachmentComponent } from '../../create-new/new-attachment/new-attachment.component';

@Component({
  selector: 'app-attachment-connections',
  templateUrl: './attachment-connections.component.html',
  styleUrls: ['./attachment-connections.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatSortModule,
    MatDialogModule
  ]
})
export class AttachmentConnectionsComponent implements OnInit, OnChanges {

  @Input() attachmentObjectArray: Attachment[] = [];
  @Output() attachmentUpdated = new EventEmitter<void>();

  private dialog = inject(MatDialog);

  displayedColumns: string[] = ['createdDate', 'name', 'type', 'owner', 'description', 'edit', 'view'];
  dataSource: any[] = [];

  ngOnInit(): void {
    this.updateDataSource();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['attachmentObjectArray']) {
      //console.log('AttachmentConnections attachmentObjectArray changed:', this.attachmentObjectArray?.length);
      this.updateDataSource();
    }
  }

  private updateDataSource(): void {
    // Extract unique attachments and flatten the structure for table display
    const uniqueAttachments = Array.from(new Set(this.attachmentObjectArray));

    // Map to a flattened structure that's easier for the table to work with
    this.dataSource = uniqueAttachments.map((att: any) => ({
      raw: att, // Keep the full object for viewing
      createdDate: att.attachmentData?.createdDate || att.createdDate,
      name: att.attachmentData?.name || att.name,
      type: att.attachmentData?.attachmentType || att.attachmentType,
      owner: att.attachmentData?.owner?.friendlyName || att.owner?.friendlyName || '',
      description: att.attachmentData?.description || att.description
    }));

    // Sort by creation date descending (newest first)
    this.dataSource.sort((a, b) => {
      const dateA = new Date(a.createdDate || 0).getTime();
      const dateB = new Date(b.createdDate || 0).getTime();
      return dateB - dateA;
    });

    //console.log('AttachmentConnections dataSource:', this.dataSource.length);
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    const friendlyDate = new Intl.DateTimeFormat('en-US', options).format(date);
    return friendlyDate !== 'Invalid Date' ? friendlyDate : dateString;
  }

  viewAttachment(row: any): void {
    // Use the raw attachment object we stored
    this.dialog.open(AttachmentViewerComponent, {
      width: '1000px',
      height: '1000px',
      data: { attachment: row.raw }
    });
  }

  editAttachment(row: any): void {
    // Open the NewAttachmentComponent dialog in edit mode
    const dialogRef = this.dialog.open(NewAttachmentComponent, {
      width: '500px',
      disableClose: true,
      data: {
        title: 'Edit Attachment',
        attachmentData: row.raw,
        entityObject: null
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        //console.log('Attachment updated:', result);
        // Emit event to parent to refresh the attachments list
        this.attachmentUpdated.emit();
      }
    });
  }

  onSortChange(sort: Sort): void {
    if (!sort.active || sort.direction === '') {
      this.updateDataSource();
      return;
    }

    this.dataSource = [...this.dataSource].sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'createdDate':
          return this.compare(new Date(a.createdDate).getTime(), new Date(b.createdDate).getTime(), isAsc);
        case 'name':
          return this.compare(a.name, b.name, isAsc);
        case 'type':
          return this.compare(a.type, b.type, isAsc);
        case 'owner':
          return this.compare(a.owner, b.owner, isAsc);
        case 'description':
          return this.compare(a.description, b.description, isAsc);
        default:
          return 0;
      }
    });
  }

  private compare(a: any, b: any, isAsc: boolean): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
}
